import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PaymentType, type PaymentStatus } from "@/api-v3";
import { Badge } from "@/components/ui/badge";
import { PaymentTypeIcon } from "@/features/payments/components/payment-type-icon";
import { getStatusColor } from "@/features/payments/helpers/get-status-color";
import { formatCurrency } from "@/lib/currency-formatter";
import { formatISODate } from "@/lib/date-formatter";
import { cn, formatKeyText } from "@/lib/utils";

import type { PaymentKind } from "../types";

interface PaymentHeaderProps {
  paymentKind: PaymentKind;
  amount: string;
  adjustedAmount?: string;
  currency: string;
  status: string;
  createdAt?: string;
  raisedByStudyTeam?: boolean;
}

const PAYMENT_TYPE: Record<PaymentKind, PaymentType> = {
  mileage: PaymentType.mileage_claim,
  receipt: PaymentType.receipt_claim,
  micropayment: PaymentType.micropayment,
  stipend: PaymentType.stipend,
};

const TITLE_KEY: Record<PaymentKind, string> = {
  mileage: "mileageClaim",
  receipt: "receiptClaim",
  micropayment: "micropayment",
  stipend: "stipend",
};

export const PaymentHeader = ({
  paymentKind,
  amount,
  adjustedAmount,
  currency,
  status,
  createdAt,
  raisedByStudyTeam = false,
}: PaymentHeaderProps) => {
  const { t } = useTranslation();
  const formattedAmount = useMemo(
    () => formatCurrency(adjustedAmount ?? amount, currency),
    [adjustedAmount, amount, currency]
  );

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <PaymentTypeIcon type={PAYMENT_TYPE[paymentKind]} />
        <div className="min-w-0 pt-0.5 space-y-1">
          <h2 className="text-lg font-semibold leading-tight truncate">
            {t(TITLE_KEY[paymentKind])}
          </h2>
          {createdAt && (
            <p className="text-sm text-muted-foreground">
              {formatISODate(createdAt, { format: "date" })}
            </p>
          )}
          {raisedByStudyTeam && (
            <Badge variant="secondary">{t("payments_from_study_team")}</Badge>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <p className="text-2xl font-semibold font-mono leading-tight">
          {formattedAmount}
        </p>
        {adjustedAmount && (
          <p className="whitespace-nowrap text-right text-sm text-muted-foreground">
            <del>
              <span className="font-mono whitespace-nowrap">
                {formatCurrency(amount, currency)}
              </span>{" "}
              <span className="lowercase">
                {t("claim_detail_claimed_amount")}
              </span>
            </del>
          </p>
        )}
        {status && (
          <Badge className={cn(getStatusColor(status as PaymentStatus))}>
            {formatKeyText(t(status))}
          </Badge>
        )}
      </div>
    </div>
  );
};
